import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/backendConfig';

// Remembers successfully loaded image URLs across component unmount/remount.
const loadedImageSrcCache = new Set();

export default function SmartImage({
  src,
  alt,
  className = '',
  containerClassName = '',
  showSkeleton = false,
  loading = 'lazy',
  fetchPriority = 'auto',
  decoding = 'async',
  onClick,
  onError,
  onLoad,
  style,
}) {
  const [currentSrc, setCurrentSrc] = useState(src || '');
  const [loaded, setLoaded] = useState(() => !!(src && loadedImageSrcCache.has(src)));
  const [proxyTried, setProxyTried] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const nextSrc = src || '';
    setCurrentSrc(nextSrc);
    setLoaded(!!(nextSrc && loadedImageSrcCache.has(nextSrc)));
    setProxyTried(false);
  }, [src]);

  // If the image is already cached, the browser may mark it as complete immediately.
  // This prevents the skeleton overlay from showing for cached images on refresh/login.
  useLayoutEffect(() => {
    try {
      const el = imgRef.current;
      if (!el) return;
      // For cached images, `complete` flips to true immediately.
      // Natural width can temporarily be 0 during the first layout pass,
      // so we key off `complete` instead.
      if (el.complete) {
        if (currentSrc) loadedImageSrcCache.add(currentSrc);
        setLoaded(true);
      }
    } catch (_) {}
  }, [currentSrc]);

  const canUseProxyFallback = useMemo(
    () => currentSrc && /^https?:\/\//i.test(currentSrc) && !currentSrc.includes('/proxy-image?'),
    [currentSrc]
  );

  return (
    <div className={`relative ${containerClassName}`}>
      {showSkeleton && !loaded && <div className="absolute inset-0 animate-pulse bg-gray-200/15" />}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={className}
        loading={loading}
        fetchpriority={fetchPriority}
        decoding={decoding}
        style={{
          ...(style || {}),
          // Keep image visible immediately; skeleton is semi-transparent.
          // When the image fails we hide the element in onError.
          transition: (style && style.transition) ? style.transition : 'filter 200ms ease',
          filter: loaded ? 'none' : 'none',
        }}
        // Prevent the browser "broken image" icon from flashing before load succeeds.
        // We keep the skeleton visible while !loaded.
        onContextMenu={(e) => {
          // Some images are right-click selectable; preserve default but don't interfere.
          if (onClick) e.preventDefault();
        }}
        onClick={onClick}
        onLoad={(e) => {
          if (currentSrc) loadedImageSrcCache.add(currentSrc);
          setLoaded(true);
          if (onLoad) onLoad(e);
        }}
        onError={(e) => {
          if (!proxyTried && canUseProxyFallback) {
            setProxyTried(true);
            setCurrentSrc(`${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(currentSrc)}`);
            return;
          }
          // Hide broken image icon; keep skeleton/empty state visible.
          try {
            e.currentTarget.style.display = 'none';
          } catch (_) {}
          if (onError) onError(e);
        }}
      />
    </div>
  );
}
